// Inbound contracts (Milestone 3, PRD §4).
import { z } from 'zod';
import { pageQuery, uuid } from './common.js';
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const text = (max) => z.string().trim().max(max);
const optText = (max) => text(max).nullable().optional();
export const RECEIPT_TYPES = ['asn', 'po', 'blind'];
export const RECEIPT_STATUSES = [
    'expected',
    'arrived',
    'receiving',
    'closed',
    'cancelled',
];
export const VARIANCE_TYPES = ['over', 'short', 'damaged', 'unexpected'];
export const PUTAWAY_STRATEGIES = ['fixed_home', 'consolidate', 'zone', 'nearest_empty'];
export const receiptLineInput = z.object({
    /** Item by id, or by SKU within the receipt's client. */
    itemId: uuid.optional(),
    sku: text(60).optional(),
    qty: z.coerce.number().int().min(1).max(10_000_000),
    /** Pack level the quantity is in (EA, CS, PL…); converted to base units. */
    uom: text(10).optional(),
    poLine: optText(30),
    lot: optText(40),
    expiry: isoDate.nullable().optional(),
    /** SSCC of the pallet carrying this line (ASN), for one-scan receiving. */
    sscc: z
        .string()
        .regex(/^\d{18}$/, 'SSCC is 18 digits')
        .nullable()
        .optional(),
});
export const createReceiptRequest = z
    .object({
    siteId: uuid,
    clientId: uuid,
    number: z
        .string()
        .trim()
        .min(1)
        .max(40)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._/-]*$/, 'Letters, digits, ".", "_", "/" or "-"'),
    type: z.enum(RECEIPT_TYPES).default('asn'),
    supplierId: uuid.nullable().optional(),
    poNumber: optText(40),
    reference: optText(60),
    expectedDate: isoDate.nullable().optional(),
    carrier: optText(60),
    notes: optText(1000),
    lines: z.array(receiptLineInput).max(2000).default([]),
})
    .refine((r) => r.type === 'blind' || r.lines.length > 0, {
    message: 'Add at least one line (or create a blind receipt)',
    path: ['lines'],
});
export const receiptQuery = pageQuery.extend({
    siteId: uuid.optional(),
    clientId: uuid.optional(),
    status: z.enum([...RECEIPT_STATUSES, 'open']).optional(),
    q: text(60).optional(),
    expectedFrom: isoDate.optional(),
    expectedTo: isoDate.optional(),
});
export const checkInRequest = z.object({
    doorLocationId: uuid,
    carrier: optText(60),
    trailerNo: optText(30),
    sealNo: optText(30),
    driverName: optText(80),
    unloadType: z.enum(['floor', 'palletized']).default('palletized'),
    containerSize: z
        .enum(['20ft', '40ft', '45ft', '53ft', 'box_truck', 'parcel'])
        .nullable()
        .optional(),
});
export const receiveRequest = z
    .object({
    /** Identify what is being received: a line, an item, or a scanned barcode. */
    lineId: uuid.optional(),
    itemId: uuid.optional(),
    barcode: text(120).optional(),
    qty: z.coerce.number().int().min(1).max(1_000_000).optional(),
    uom: text(10).optional(),
    lot: optText(40),
    expiry: isoDate.nullable().optional(),
    /** Serial-controlled items: one serial per unit (qty = serials.length). */
    serials: z.array(text(60).min(1)).max(500).optional(),
    catchWeightG: z.coerce.number().int().min(1).max(10_000_000).nullable().optional(),
    countryOfOrigin: z
        .string()
        .regex(/^[A-Z]{2}$/, 'Two-letter country code')
        .nullable()
        .optional(),
    condition: z.enum(['good', 'damaged']).default('good'),
    reasonCodeId: uuid.nullable().optional(),
    note: optText(500),
    attachmentIds: z.array(uuid).max(10).default([]),
    /** Scan an existing LPN to build a pallet; omit to start a new one. */
    lpnCode: text(30).optional(),
    lpnType: z.enum(['pallet', 'case', 'tote']).default('pallet'),
    /** Dock or receiving location (defaults to the check-in door). */
    locationId: uuid.optional(),
})
    .refine((r) => r.lineId || r.itemId || r.barcode, {
    message: 'Scan a barcode or choose a line or item',
    path: ['barcode'],
});
export const receiveSsccRequest = z.object({
    sscc: text(60).min(1),
    locationId: uuid.optional(),
});
export const varianceDecision = z.object({ note: optText(500) });
export const closeReceiptRequest = z.object({ note: optText(500) });
export const importReceiptsRequest = z.object({
    csv: z.string().min(1).max(15_000_000),
    dryRun: z.boolean().default(true),
    siteId: uuid,
    clientId: uuid,
});
export const putawayQuery = pageQuery.extend({
    siteId: uuid.optional(),
    state: z.enum(['open', 'done', 'cancelled']).default('open'),
    receiptId: uuid.optional(),
    q: text(40).optional(),
});
export const confirmPutawayRequest = z
    .object({
    locationId: uuid.optional(),
    /** Scanned location barcode. */
    locationCode: text(40).optional(),
    reasonCodeId: uuid.nullable().optional(),
    note: optText(500),
})
    .refine((r) => r.locationId || r.locationCode, {
    message: 'Scan or choose the location',
    path: ['locationCode'],
});
export const putawayRuleInput = z.object({
    siteId: uuid,
    clientId: uuid.nullable().optional(),
    storageClass: optText(30),
    temperatureClass: z.enum(['ambient', 'chilled', 'frozen']).nullable().optional(),
    velocityClass: z.enum(['A', 'B', 'C']).nullable().optional(),
    strategy: z.enum(PUTAWAY_STRATEGIES),
    zone: optText(20),
    locationType: z.enum(['rack', 'shelving', 'floor', 'flow_rack']).nullable().optional(),
    priority: z.coerce.number().int().min(1).max(999).default(100),
    active: z.boolean().default(true),
});
export const homeLocationInput = z.object({
    siteId: uuid,
    itemId: uuid,
    locationId: uuid,
    minQty: z.coerce.number().int().min(0).max(1_000_000).default(0),
    maxQty: z.coerce.number().int().min(0).max(1_000_000).default(0),
});
export const uploadAttachmentRequest = z.object({
    filename: text(120).min(1),
    contentType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
    /** Base64 file content (≤ 5 MB decoded). */
    data: z.string().min(4).max(7_000_000),
    clientId: uuid.nullable().optional(),
    siteId: uuid.nullable().optional(),
});
//# sourceMappingURL=inbound.js.map